import React, { useCallback, useEffect, useState } from 'react';
import {
	Card,
	CardContent,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Chip,
	IconButton,
	Button,
	Stack,
	Tooltip,
	Alert,
	Snackbar,
	TextField,
	MenuItem,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RefreshIcon from '@mui/icons-material/Refresh';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import {
	listCredentialRequests,
	revokeCredentialRequest,
	deleteCredentialRequest,
	createSetupSession,
	statusChoices,
	type CredentialRequest,
	type CreateCredentialRequestOutput,
} from '../../Services/credentialRequests';
import CreateCredentialRequestModal from './CreateCredentialRequestModal';

const statusColors: Record<string, 'warning' | 'success' | 'error' | 'default'> = {
	pending: 'warning',
	completed: 'success',
	expired: 'error',
	revoked: 'default',
};

const formatDate = (dateStr: string) => {
	try {
		return new Date(dateStr).toLocaleDateString('fr-FR', {
			day: '2-digit',
			month: '2-digit',
			year: 'numeric',
			hour: '2-digit',
			minute: '2-digit',
		});
	} catch {
		return dateStr;
	}
};

export const CredentialRequestsList: React.FC = () => {
	const [requests, setRequests] = useState<CredentialRequest[]>([]);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [filterStatus, setFilterStatus] = useState('');
	const [filterSku, setFilterSku] = useState('');

	// Modals
	const [createOpen, setCreateOpen] = useState(false);
	const [confirmDelete, setConfirmDelete] = useState<CredentialRequest | null>(null);
	const [setupTarget, setSetupTarget] = useState<CredentialRequest | null>(null);
	const [setupFolderId, setSetupFolderId] = useState('');
	const [setupLoading, setSetupLoading] = useState(false);

	// Notifications
	const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

	const fetchData = useCallback(async () => {
		setLoading(true);
		try {
			const res = await listCredentialRequests({
				status: filterStatus || undefined,
				sku: filterSku || undefined,
				limit: 50,
			});
			setRequests(res.requests || []);
			setTotal(res.total || 0);
		} catch (err: any) {
			setSnackbar({ message: err?.message || 'Failed to load', severity: 'error' });
		} finally {
			setLoading(false);
		}
	}, [filterStatus, filterSku]);

	useEffect(() => {
		fetchData();
	}, [fetchData]);

	const handleCopyLink = (publicUrl: string) => {
		navigator.clipboard.writeText(publicUrl);
		setSnackbar({ message: 'Link copied to clipboard', severity: 'success' });
	};

	const handleRevoke = async (req: CredentialRequest) => {
		try {
			await revokeCredentialRequest(req.id);
			setSnackbar({ message: 'Credential request revoked', severity: 'success' });
			fetchData();
		} catch (err: any) {
			setSnackbar({ message: err?.error || err?.message || 'Failed to revoke', severity: 'error' });
		}
	};

	const handleDelete = async (req: CredentialRequest) => {
		try {
			await deleteCredentialRequest(req.id);
			setSnackbar({ message: 'Credential request deleted', severity: 'success' });
			setConfirmDelete(null);
			fetchData();
		} catch (err: any) {
			setSnackbar({ message: err?.error || err?.message || 'Failed to delete', severity: 'error' });
		}
	};

	const handleSetup = async () => {
		if (!setupTarget || !setupFolderId.trim()) return;
		setSetupLoading(true);
		try {
			const res = await createSetupSession(setupTarget.id, setupFolderId.trim());
			setSetupTarget(null);
			setSetupFolderId('');
			window.open(res.redirectUrl, '_blank');
		} catch (err: any) {
			setSnackbar({ message: err?.error || err?.message || 'Failed to create setup session', severity: 'error' });
		} finally {
			setSetupLoading(false);
		}
	};

	const handleCreated = (result: CreateCredentialRequestOutput) => {
		setCreateOpen(false);
		setSnackbar({ message: 'Credential request created', severity: 'success' });
		// Copy link immediately
		navigator.clipboard.writeText(result.publicUrl);
		fetchData();
	};

	return (
		<>
			<Card>
				<CardContent>
					<Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
						<Typography variant="h5">Credential Requests ({total})</Typography>
						<Stack direction="row" spacing={1}>
							<IconButton onClick={fetchData} size="small">
								<RefreshIcon />
							</IconButton>
							<Button
								variant="contained"
								startIcon={<AddIcon />}
								onClick={() => setCreateOpen(true)}
							>
								New Request
							</Button>
						</Stack>
					</Stack>

					{/* Filters */}
					<Stack direction="row" spacing={2} sx={{ mb: 2 }}>
						<TextField
							label="Status"
							select
							value={filterStatus}
							onChange={(e) => setFilterStatus(e.target.value)}
							size="small"
							sx={{ minWidth: 150 }}
						>
							<MenuItem value="">All</MenuItem>
							{statusChoices.map((c) => (
								<MenuItem key={c.id} value={c.id}>
									{c.name}
								</MenuItem>
							))}
						</TextField>
						<TextField
							label="SKU"
							value={filterSku}
							onChange={(e) => setFilterSku(e.target.value)}
							size="small"
							placeholder="Filter by SKU"
						/>
					</Stack>

					{/* Table */}
					<TableContainer>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>ID</TableCell>
									<TableCell>SKU</TableCell>
									<TableCell>Client</TableCell>
									<TableCell>Status</TableCell>
									<TableCell>Created</TableCell>
									<TableCell>Expires</TableCell>
									<TableCell>Notify</TableCell>
									<TableCell align="right">Actions</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{loading ? (
									<TableRow>
										<TableCell colSpan={8} align="center">
											Loading...
										</TableCell>
									</TableRow>
								) : requests.length === 0 ? (
									<TableRow>
										<TableCell colSpan={8} align="center">
											No credential requests
										</TableCell>
									</TableRow>
								) : (
									requests.map((req) => (
										<TableRow key={req.id} hover>
											<TableCell>{req.id}</TableCell>
											<TableCell>
												<Typography variant="body2" fontWeight="bold">
													{req.sku}
												</Typography>
												<Typography variant="caption" color="textSecondary">
													{req.skuParent}
												</Typography>
											</TableCell>
											<TableCell>{req.clientName || '-'}</TableCell>
											<TableCell>
												<Chip
													label={req.status}
													color={statusColors[req.status] || 'default'}
													size="small"
												/>
											</TableCell>
											<TableCell>{formatDate(req.createdAt)}</TableCell>
											<TableCell>{formatDate(req.expiresAt)}</TableCell>
											<TableCell>
												{req.notifyEmails?.map((e) => (
													<Chip key={e} label={e} size="small" variant="outlined" sx={{ mr: 0.5, mb: 0.5 }} />
												))}
											</TableCell>
											<TableCell align="right">
												<Stack direction="row" spacing={0.5} justifyContent="flex-end">
													<Tooltip title="Copy public link">
														<IconButton
															size="small"
															onClick={() => handleCopyLink(req.publicUrl)}
														>
															<ContentCopyIcon fontSize="small" />
														</IconButton>
													</Tooltip>
													{req.status === 'completed' && (
														<Tooltip title="Setup connector">
															<IconButton
																size="small"
																color="primary"
																onClick={() => setSetupTarget(req)}
															>
																<RocketLaunchIcon fontSize="small" />
															</IconButton>
														</Tooltip>
													)}
													{req.status === 'pending' && (
														<Tooltip title="Revoke">
															<IconButton
																size="small"
																color="warning"
																onClick={() => handleRevoke(req)}
															>
																<BlockIcon fontSize="small" />
															</IconButton>
														</Tooltip>
													)}
													<Tooltip title="Delete">
														<IconButton
															size="small"
															color="error"
															onClick={() => setConfirmDelete(req)}
														>
															<DeleteIcon fontSize="small" />
														</IconButton>
													</Tooltip>
												</Stack>
											</TableCell>
										</TableRow>
									))
								)}
							</TableBody>
						</Table>
					</TableContainer>
				</CardContent>
			</Card>

			{/* Create modal */}
			<CreateCredentialRequestModal
				open={createOpen}
				onClose={() => setCreateOpen(false)}
				onCreated={handleCreated}
			/>

			{/* Confirm delete dialog */}
			<Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)}>
				<DialogTitle>Delete credential request</DialogTitle>
				<DialogContent>
					Are you sure you want to delete request #{confirmDelete?.id} ({confirmDelete?.sku})?
					This action is irreversible.
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
					<Button
						color="error"
						variant="contained"
						onClick={() => confirmDelete && handleDelete(confirmDelete)}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			{/* Setup session dialog */}
			<Dialog
				open={!!setupTarget}
				onClose={() => { setSetupTarget(null); setSetupFolderId(''); }}
				maxWidth="sm"
				fullWidth
			>
				<DialogTitle>Setup connector - {setupTarget?.sku}</DialogTitle>
				<DialogContent>
					<Typography variant="body2" sx={{ mb: 2 }}>
						Create a setup session with pre-filled credentials from this deposit
						(client: {setupTarget?.clientName || 'N/A'}).
					</Typography>
					<TextField
						label="Project Folder ID"
						placeholder="pXXXXXXXX"
						value={setupFolderId}
						onChange={(e) => setSetupFolderId(e.target.value)}
						fullWidth
						autoFocus
						size="small"
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => { setSetupTarget(null); setSetupFolderId(''); }}>
						Cancel
					</Button>
					<Button
						variant="contained"
						disabled={!setupFolderId.trim() || setupLoading}
						onClick={handleSetup}
						startIcon={<RocketLaunchIcon />}
					>
						{setupLoading ? 'Creating...' : 'Setup'}
					</Button>
				</DialogActions>
			</Dialog>

			{/* Snackbar notifications */}
			<Snackbar
				open={!!snackbar}
				autoHideDuration={4000}
				onClose={() => setSnackbar(null)}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				{snackbar ? (
					<Alert severity={snackbar.severity} onClose={() => setSnackbar(null)}>
						{snackbar.message}
					</Alert>
				) : undefined}
			</Snackbar>
		</>
	);
};
